import React, { useState, useEffect } from 'react';
import { Calendar, Clock, ArrowRight, CreditCard } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { services, timeSlots, vehicleTypes, serviceAddons, TRAVEL_FEE } from '../utils/mock';
import { toast } from 'sonner';
import { trackBookingSubmission } from '../utils/analytics';
import { PaymentForm, CreditCard as SquareCreditCard } from 'react-square-web-payments-sdk';

// Import booking sub-components
import InfoBanners from './booking/InfoBanners';
import CustomerInfoSection from './booking/CustomerInfoSection';
import ServiceSelectionSection from './booking/ServiceSelectionSection';
import PaymentOptionsSection from './booking/PaymentOptionsSection';
import PriceSummary from './booking/PriceSummary';

const Booking = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    vehicleType: '',
    service: '',
    addons: [],
    date: '',
    time: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [referralValid, setReferralValid] = useState(null);
  const [loyaltyStatus, setLoyaltyStatus] = useState(null);
  const [checkingLoyalty, setCheckingLoyalty] = useState(false);
  
  // Square payment states
  const [paymentOption, setPaymentOption] = useState('pay-later'); // 'full', 'deposit', 'pay-later'
  const [showPayment, setShowPayment] = useState(false);
  const [bookingId, setBookingId] = useState(null);
  const [squareConfig, setSquareConfig] = useState(null);

  // Fetch Square configuration
  useEffect(() => {
    const fetchSquareConfig = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${API_URL}/api/square/config`);
        if (response.ok) {
          const config = await response.json();
          setSquareConfig(config);
        }
      } catch (error) {
        console.error('Failed to fetch Square config:', error);
      }
    };
    fetchSquareConfig();
  }, []);

  // Check customer loyalty status when email changes
  const checkLoyaltyStatus = async (email) => {
    if (!email || !email.includes('@')) return;
    
    setCheckingLoyalty(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/customers/lookup/${encodeURIComponent(email)}`);
      
      if (response.ok) {
        const data = await response.json();
        setLoyaltyStatus(data);
      }
    } catch (error) {
      console.error('Failed to check loyalty status:', error);
    } finally {
      setCheckingLoyalty(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Check loyalty when email is entered
    if (name === 'email' && value.includes('@')) {
      checkLoyaltyStatus(value);
    }
  };

  // Calculate total price with discounts
  const calculateTotal = () => {
    if (!formData.vehicleType || !formData.service) return null;
    
    // Get service price
    const selectedService = services.find(s => s.title === formData.service);
    const servicePrice = selectedService?.pricing[formData.vehicleType] || 0;
    
    // Get addons total
    const addonsTotal = formData.addons.reduce((sum, addonTitle) => {
      const addon = serviceAddons.find(a => a.title === addonTitle);
      return sum + (addon?.pricing[formData.vehicleType] || 0);
    }, 0);
    
    const subtotal = servicePrice + addonsTotal + TRAVEL_FEE;
    
    // Apply loyalty discount
    let loyaltyDiscount = 0;
    if (loyaltyStatus?.exists && loyaltyStatus.discount_rate > 0) {
      const discountableAmount = servicePrice + addonsTotal;
      loyaltyDiscount = discountableAmount * loyaltyStatus.discount_rate;
    }
    
    // Apply first-time customer discount (auto-applied for new customers)
    let referralDiscount = 0;
    if (!loyaltyStatus?.exists) {
      referralDiscount = 30;
    }
    
    const total = subtotal - loyaltyDiscount - referralDiscount;
    
    return {
      servicePrice,
      addonsTotal,
      travelFee: TRAVEL_FEE,
      subtotal,
      loyaltyDiscount,
      referralDiscount,
      total: Math.max(0, total)
    };
  };

  const priceBreakdown = calculateTotal();

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddonChange = (addonTitle) => {
    setFormData(prev => {
      const currentAddons = prev.addons;
      if (currentAddons.includes(addonTitle)) {
        // Remove addon if already selected
        return { ...prev, addons: currentAddons.filter(a => a !== addonTitle) };
      } else {
        // Add addon if not selected
        return { ...prev, addons: [...currentAddons, addonTitle] };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const result = await response.json();
        
        // Track successful booking submission
        trackBookingSubmission({
          vehicleType: formData.vehicleType,
          service: formData.service,
          addons: formData.addons
        });
        
        // Store booking ID for payment
        setBookingId(result.id);
        
        // If payment is required now, show payment form
        if (paymentOption === 'full' || paymentOption === 'deposit') {
          setShowPayment(true);
          toast.success('Booking created! Please complete payment.');
        } else {
          // Pay later - booking is complete
          toast.success('Booking confirmed!', {
            description: `Your booking has been received. Booking ID: ${result.id.slice(0, 8)}`
          });
          resetForm();
        }
      } else {
        const error = await response.json();
        toast.error('Booking failed', {
          description: error.detail || 'Please try again or contact us directly.'
        });
      }
    } catch (error) {
      toast.error('Booking failed. Please try again or call us at 236-335-7663.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      address: '',
      vehicleType: '',
      service: '',
      addons: [],
      date: '',
      time: '',
      notes: '',
      referral_code: ''
    });
    setReferralValid(null);
    setShowPayment(false);
    setBookingId(null);
    setPaymentOption('pay-later');
  };

  const handlePaymentSuccess = (paymentData) => {
    toast.success('Payment successful!', {
      description: `Thank you! Your booking is confirmed.`
    });
    resetForm();
  };

  const handlePaymentError = (error) => {
    toast.error('Payment failed', {
      description: error.message || 'Please try again or contact us.'
    });
  };

  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  return (
    <section id="booking" className="py-12 md:py-24 bg-zinc-950">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-8 md:mb-16">
          <div className="inline-block bg-red-600/20 border border-red-500/50 rounded-full px-3 py-1.5 md:px-4 md:py-2 mb-2 md:mb-4">
            <span className="text-red-500 font-semibold text-xs md:text-sm">Book Your Service</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-2 md:mb-4">
            Schedule Your Detail
          </h2>
          <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto">
            Choose your service and preferred time. We'll come to your location.
          </p>
        </div>

        {/* Booking Form */}
        <div className="max-w-4xl mx-auto">
          {/* Info Banners Component */}
          <InfoBanners />

          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-2xl text-white">Booking Details</CardTitle>
              <CardDescription className="text-gray-400">
                Fill in your information and we'll confirm your appointment shortly
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">                {/* Customer Information Section Component */}
                <CustomerInfoSection 
                  formData={formData}
                  handleInputChange={handleInputChange}
                  handleSelectChange={handleSelectChange}
                  loyaltyStatus={loyaltyStatus}
                />                {/* Service Selection Section Component */}
                <ServiceSelectionSection
                  formData={formData}
                  handleSelectChange={handleSelectChange}
                  handleAddonChange={handleAddonChange}
                />



                {/* Additional Notes */}
                <div className="space-y-2">
                  <Label htmlFor="notes" className="text-white">
                    Additional Notes (Optional)
                  </Label>
                  <Textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="Any special requests or additional information..."
                    rows={4}
                    className="bg-zinc-800 border-zinc-700 text-white placeholder:text-gray-500"
                  />
                </div>

                {/* Price Summary */}
                {priceBreakdown && (
                  <PriceSummary 
                    priceBreakdown={priceBreakdown}
                    formData={formData}
                    loyaltyStatus={loyaltyStatus}
                  />
                )}


                {/* Payment Options Section Component */}
                {!showPayment && (
                  <PaymentOptionsSection
                    paymentOption={paymentOption}
                    setPaymentOption={setPaymentOption}
                    priceBreakdown={priceBreakdown}
                  />
                )}

                {/* Square Payment Form */}
                {showPayment && squareConfig && bookingId && (
                  <div className="space-y-4">
                    <Card className="bg-zinc-900 border-zinc-800">
                      <CardHeader>
                        <CardTitle className="text-white flex items-center">
                          <CreditCard className="w-5 h-5 mr-2" />
                          Complete Payment
                        </CardTitle>
                        <CardDescription className="text-gray-400">
                          Amount to pay: <span className="text-white font-bold text-lg">
                            ${priceBreakdown.total.toFixed(2)}
                          </span>
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <PaymentForm
                          applicationId={squareConfig.application_id}
                          locationId={squareConfig.location_id}
                          cardTokenizeResponseReceived={async (token, verifiedBuyer) => {
                            try {
                              const API_URL = process.env.REACT_APP_BACKEND_URL;
                              const response = await fetch(`${API_URL}/api/square/create-payment`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                  booking_id: bookingId,
                                  source_id: token.token,
                                  payment_option: paymentOption,
                                  verification_token: verifiedBuyer?.token || null
                                }),
                              });

                              const data = await response.json();
                              if (response.ok && data.success) {
                                handlePaymentSuccess(data);
                              } else {
                                handlePaymentError(new Error(data.detail || 'Payment failed'));
                              }
                            } catch (error) {
                              handlePaymentError(error);
                            }
                          }}
                          createPaymentRequest={() => ({
                            countryCode: 'US',
                            currencyCode: 'USD',
                            total: {
                              amount: priceBreakdown.total.toFixed(2),
                              label: 'Mobile Shine Detailing',
                            },
                          })}
                        >
                          <SquareCreditCard />
                        </PaymentForm>
                        
                        <div className="mt-4 flex items-center justify-center space-x-2 text-xs text-gray-500">
                          <svg className="w-8 h-5" viewBox="0 0 38 24" fill="none">
                            <rect width="38" height="24" rx="3" fill="#1A1F2C"/>
                            <path d="M13 8h12v8H13z" fill="#FF6000"/>
                            <path d="M11 8h12v8H11z" fill="#EB001B"/>
                            <path d="M17 12a4 4 0 01-1.5-3c0-1.2.5-2.3 1.5-3a4 4 0 000 6z" fill="#F79E1B"/>
                          </svg>
                          <span>Secure payment powered by Square</span>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowPayment(false);
                        setBookingId(null);
                      }}
                      className="w-full border-zinc-700 text-white hover:bg-zinc-800"
                    >
                      Cancel Payment
                    </Button>
                  </div>
                )}

                {/* Submit Button */}
                {!showPayment && (
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-red-600 hover:bg-red-700 text-white text-lg py-6 font-semibold group"
                  >
                    {isSubmitting ? 'Submitting...' : 
                      paymentOption === 'pay-later' ? 'Confirm Booking' : 'Continue to Payment'}
                    {!isSubmitting && (
                      <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                    )}
                  </Button>
                )}
              </form>
            </CardContent>
          </Card>

          {/* Google Review Prompt After Booking */}
          <Card className="bg-gradient-to-br from-red-600/10 to-red-700/5 border-red-500/30 hover:border-red-500/50 transition-all mt-6">
            <CardContent className="p-6">
              <div className="text-center">
                <div className="flex items-center justify-center space-x-2 mb-3">
                  <span className="text-2xl">⭐</span>
                  <h3 className="text-white font-bold text-xl">Happy with Our Service?</h3>
                  <span className="text-2xl">⭐</span>
                </div>
                <p className="text-gray-300 text-sm mb-4 max-w-md mx-auto">
                  Your feedback helps us improve and helps others discover Mobile Shine. We'd love to hear about your experience!
                </p>
                <a
                  href="https://g.page/r/CS8IeFV59cyJEBM/review"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-3 rounded-lg transition-all hover:scale-105 shadow-lg"
                >
                  <span className="text-lg">📝</span>
                  <span>Leave a Google Review</span>
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Booking;
